import { z } from 'zod';

export interface StructuredErrorLog {
  code: string;
  message: string;
  details?: unknown;
  hint?: string | null;
}

export const logStructuredError = (error: StructuredErrorLog | unknown) => {
  if (typeof error === 'object' && error !== null && 'code' in error && 'message' in error) {
    const err = error as StructuredErrorLog;
    console.error(`[API Error ${err.code}] ${err.message}`, {
      details: err.details ?? null,
      hint: err.hint ?? null,
    });
  } else if (error && typeof error === 'object' && 'message' in error) {
    const err = error as { code?: string; message: string; details?: unknown; hint?: string };
    console.error(`[API Error ${err.code || 'UNKNOWN'}] ${err.message}`, {
      details: err.details ?? null,
      hint: err.hint ?? null,
    });
  } else {
    console.error('[API Error UNKNOWN]', error);
  }
};

// Zod schema for AI Insights
export const SentimentBreakdownSchema = z.object({
  positive: z.number().catch(0),
  neutral: z.number().catch(0),
  negative: z.number().catch(0),
});

export const KeyInsightSchema = z.object({
  title: z.string().catch('Insight'),
  description: z.string().catch(''),
});

export const AIInsightsSchema = z.object({
  summary: z.string().catch('No summary available.'),
  key_insights: z.array(KeyInsightSchema).catch([]),
  top_requests: z.array(z.string()).catch([]),
  customer_sentiment_breakdown: SentimentBreakdownSchema.catch({ positive: 0, neutral: 0, negative: 0 }),
  recommended_actions: z.array(z.string()).catch([]),
});

export type AIInsightsData = z.infer<typeof AIInsightsSchema>;

export const DEFAULT_AI_INSIGHTS: AIInsightsData = {
  summary: 'No AI summary available yet.',
  key_insights: [],
  top_requests: [],
  customer_sentiment_breakdown: {
    positive: 0,
    neutral: 0,
    negative: 0,
  },
  recommended_actions: [],
};

export const normalizeAIInsights = (raw: unknown): AIInsightsData => {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_AI_INSIGHTS;
  }

  const parseResult = AIInsightsSchema.safeParse(raw);
  if (!parseResult.success) {
    logStructuredError({
      code: 'AI_INSIGHTS_VALIDATION_WARNING',
      message: 'Failed to validate AI insights JSON structure. Falling back to safe defaults.',
      details: parseResult.error.format(),
      hint: 'Ensure AI response complies with expected schema.',
    });
    const rawObj = raw as Record<string, unknown>;
    const rawSentiment = (typeof rawObj.customer_sentiment_breakdown === 'object' && rawObj.customer_sentiment_breakdown !== null)
      ? (rawObj.customer_sentiment_breakdown as Record<string, unknown>)
      : {};

    return {
      summary: typeof rawObj.summary === 'string' ? rawObj.summary : DEFAULT_AI_INSIGHTS.summary,
      key_insights: Array.isArray(rawObj.key_insights)
        ? rawObj.key_insights.map(item => typeof item === 'object' && item !== null ? {
            title: String((item as Record<string, unknown>).title || 'Insight'),
            description: String((item as Record<string, unknown>).description || '')
          } : { title: 'Insight', description: String(item) })
        : [],
      top_requests: Array.isArray(rawObj.top_requests) ? rawObj.top_requests.map(String) : [],
      customer_sentiment_breakdown: {
        positive: typeof rawSentiment.positive === 'number' ? rawSentiment.positive : 0,
        neutral: typeof rawSentiment.neutral === 'number' ? rawSentiment.neutral : 0,
        negative: typeof rawSentiment.negative === 'number' ? rawSentiment.negative : 0,
      },
      recommended_actions: Array.isArray(rawObj.recommended_actions) ? rawObj.recommended_actions.map(String) : [],
    };
  }

  return parseResult.data;
};

// Zod schema for individual Submission AI Analysis
export const AISubmissionAnalysisSchema = z.object({
  sentiment: z.enum(['positive', 'neutral', 'negative']).catch('neutral'),
  intent: z.string().catch('General Inquiry'),
  summary: z.string().catch(''),
  priority: z.enum(['low', 'medium', 'high']).catch('medium'),
  key_items: z.array(z.string()).catch([]),
});

export type AISubmissionAnalysisData = z.infer<typeof AISubmissionAnalysisSchema>;

export const DEFAULT_AI_SUBMISSION_ANALYSIS: AISubmissionAnalysisData = {
  sentiment: 'neutral',
  intent: 'General Inquiry',
  summary: 'No analysis available.',
  priority: 'medium',
  key_items: [],
};

export const normalizeAISubmissionAnalysis = (raw: unknown): AISubmissionAnalysisData => {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_AI_SUBMISSION_ANALYSIS;
  }

  const parseResult = AISubmissionAnalysisSchema.safeParse(raw);
  if (!parseResult.success) {
    const rawObj = raw as Record<string, unknown>;
    const rawSentiment = String(rawObj.sentiment || '').toLowerCase();
    const rawPriority = String(rawObj.priority || '').toLowerCase();

    return {
      sentiment: (['positive', 'neutral', 'negative'].includes(rawSentiment) ? rawSentiment : 'neutral') as 'positive' | 'neutral' | 'negative',
      intent: typeof rawObj.intent === 'string' ? rawObj.intent : 'General Inquiry',
      summary: typeof rawObj.summary === 'string' ? rawObj.summary : '',
      priority: (['low', 'medium', 'high'].includes(rawPriority) ? rawPriority : 'medium') as 'low' | 'medium' | 'high',
      key_items: Array.isArray(rawObj.key_items) ? rawObj.key_items.map(String) : [],
    };
  }

  return parseResult.data;
};
